import { useTranslation } from 'react-i18next';

import { ReactComponent as CheckQuoteIcon } from '../../../../../../assets/icons/check.svg';
import { ReactComponent as EditIcon } from '../../../../../../assets/icons/edit.svg';
import { ReactComponent as IconDownload } from '../../../../../../assets/icons/icon-download.svg';
import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import Tooltip from '../../../../../../shared/components/Globals/Tooltip/Tooltip';
import type { IQuote } from '../../../../../../shared/interfaces/quotes';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import { TRANSLATIONS_QUOTES } from '../../../../../../shared/translations/quotes';
import { quoteHasAppliedCensus } from '../../../../../../shared/utils/quotes/quotes';
import './QuotationCard.scss';

interface IQuotationCardProps {
  quote: IQuote;
  onAccept: (id: number) => void;
  onEdit?: (id: number) => void;
  onDownload: (id: number) => void;
  isInteractive?: boolean;
  isAccepting?: boolean;
  isDownloading?: boolean;
  acceptDisabledTooltip?: string;
}

const QuotationCard = ({
  quote,
  onAccept,
  onEdit,
  onDownload,
  isInteractive = true,
  isAccepting = false,
  isDownloading = false,
  acceptDisabledTooltip
}: IQuotationCardProps) => {
  const { t } = useTranslation();
  const isAccepted = quote.status === 'accepted';

  const isEditable = Boolean(onEdit) && !quoteHasAppliedCensus(quote);

  const cardClassName = ['quotation-card', isAccepted ? 'quotation-card--accepted' : ''].filter(Boolean).join(' ');

  return (
    <div className={cardClassName}>
      <div className="quotation-card__row">
        <div className="quotation-card__info">
          <div className="quotation-card__name">{quote.name}</div>
          <div className="quotation-card__segment">
            {quote.segment || t(TRANSLATIONS_QUOTES.create.group.segmentAll)}
          </div>
        </div>

        <div className="quotation-card__actions">
          <CustomButton
            variant="default"
            size="s"
            className="quotation-card__action-btn"
            onClick={() => onDownload(quote.id)}
            loading={isDownloading}
            disabled={isAccepting || isDownloading}
            loadingText={t(TRANSLATIONS_QUOTES.create.createSuccessModal.downloading)}
          >
            {t(TRANSLATIONS_PROSPECTS.detail.quotationCard.downloadPdf)}
            <IconDownload className="quotation-card__icon-download" />
          </CustomButton>

          {isEditable && (
            <CustomButton
              variant="default"
              size="s"
              className="quotation-card__action-btn"
              onClick={() => onEdit?.(quote.id)}
              disabled={isAccepting}
            >
              {t(TRANSLATIONS_PROSPECTS.detail.quotationCard.edit)}
              <EditIcon className="quotation-card__edit" />
            </CustomButton>
          )}

          {isInteractive && !isAccepted && (
            <Tooltip
              content={acceptDisabledTooltip ?? ''}
              disabled={!acceptDisabledTooltip}
              triggerClassName="quotation-card__accept-tooltip"
            >
              <CustomButton
                variant="default"
                onClick={() => onAccept(quote.id)}
                className="quotation-card__accept"
                loading={isAccepting}
                disabled={Boolean(acceptDisabledTooltip)}
              >
                {t(TRANSLATIONS_PROSPECTS.detail.quotationCard.acceptQuote)}
              </CustomButton>
            </Tooltip>
          )}

          {isAccepted && isInteractive && (
            <button
              type="button"
              onClick={() => onAccept(quote.id)}
              className="quotation-card__accepted quotation-card__accepted--interactive"
            >
              <CheckQuoteIcon className="quotation-card__check" />
              <span className="quotation-card__accepted-label">
                {t(TRANSLATIONS_PROSPECTS.detail.quotationCard.clientAccepted)}
              </span>
            </button>
          )}

          {isAccepted && !isInteractive && (
            <div className="quotation-card__accepted">
              <CheckQuoteIcon className="quotation-card__check" />
              <span className="quotation-card__accepted-label">
                {t(TRANSLATIONS_PROSPECTS.detail.quotationCard.clientAccepted)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotationCard;
